import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  padding: ${theme.spaces.regular}px;
  background: ${theme.colors.indigo.superLight};
  border-radius: 16px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.large}px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spaces.small2X}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 50%;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;
`;

const StyledBody = styled.div``;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  margin-bottom: ${theme.spaces.large}px;

  > div:nth-child(2) {
    flex-grow: 1;
  }

  &:last-child {
    margin-bottom: unset;
  }
`;

const StyledCol = styled.div``;

const StyledBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-left: ${theme.spaces.smallX}px;
  padding-right: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.16)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
`;

const PaymentMethodsList = ({ loading, items, onAdd }) => {
  return (
    <StyledContainer>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.regular} weight={600}>
            Payment Methods
          </Text>
        </StyledTitleContainer>
        <StyledIconContainer onClick={onAdd}>
          <Icon alias={ICON_ALIASES.plus} color={theme.colors.gray.secondary} />
        </StyledIconContainer>
      </StyledHeader>
      {loading && (
        <StyledSpinnerContainer>
          <Spinner />
        </StyledSpinnerContainer>
      )}
      {!loading && !items?.length && (
        <StyledBody>
          <Text
            size={theme.fontSizes.small}
            weight={500}
            color={theme.colors.gray.secondary}
            bottomSpace={theme.spaces.medium}
          >
            No data
          </Text>
        </StyledBody>
      )}
      {!loading && (
        <StyledBody>
          {items?.map(({ paySourceId, cardNumber, cardMonth, cardYear, primary }) => (
            <StyledRow key={paySourceId}>
              <StyledCol>
                <Icon alias={ICON_ALIASES.card} color={theme.colors.gray.secondary} />
              </StyledCol>
              <StyledCol>
                <Text size={theme.fontSizes.regular} weight={700}>
                  **** **** **** {cardNumber}
                </Text>
                <Text size={theme.fontSizes.small} weight={500} color={theme.colors.gray.secondary}>
                  {cardMonth} / {cardYear} ***
                </Text>
              </StyledCol>
              <StyledCol>
                {primary && (
                  <StyledBadge>
                    <Text size={theme.fontSizes.smallX} weight={700} color={theme.colors.indigo.primary}>
                      Primary
                    </Text>
                  </StyledBadge>
                )}
                {!primary && <Button outlined>Make Primary</Button>}
              </StyledCol>
            </StyledRow>
          ))}
        </StyledBody>
      )}
    </StyledContainer>
  );
};

PaymentMethodsList.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      paySourceId: PropTypes.string,
      cardNumber: PropTypes.string,
      cardMonth: PropTypes.string,
      cardYear: PropTypes.string,
      primary: PropTypes.bool,
    })
  ),
  onAdd: PropTypes.func,
};

export default PaymentMethodsList;
